import React from "react";
import { Leaf, Tractor, Lightbulb, ShieldCheck, Globe, Sprout, Users, FlaskConical } from "lucide-react";
import { SectionHead } from "@/components/SectionHead";
import { Reveal, Stagger, StaggerItem, ParallaxImage, Marquee } from "@/lib/motion";
import { IMG, SEAL } from "@/data/images";
import { FoundingLeaders } from "@/components/FoundingLeaders";

const BEGINNING = [
  { icon: Leaf, title: "Nature", text: "Extraordinary nutrition already exists in nature. We just have to respect and preserve it." },
  { icon: Tractor, title: "Farmers", text: "We met incredible farmers who shared our values and our respect for the land." },
  { icon: Lightbulb, title: "The Vision", text: "To build a brand that families can trust blindly for purity, quality and honest nutrition." },
  { icon: ShieldCheck, title: "Nutribest", text: "A promise made real through clean ingredients, responsible practices and relentless care." },
  { icon: Globe, title: "The Future", text: "To impact millions of lives with better nutrition and create a healthier, more conscious world." },
];

const VALUE_CARDS = [
  { icon: Sprout, title: "Purity", text: "We choose only pure, natural ingredients. Nothing artificial. Nothing unnecessary. Just nature, in its best form.", image: IMG.heroStory },
  { icon: ShieldCheck, title: "Quality", text: "We go beyond standards to deliver excellence in every product. Because you deserve nothing less.", image: IMG.heroQuality },
  { icon: Users, title: "Transparency", text: "We believe in honest communication and complete transparency. No hidden ingredients. No hidden stories.", image: IMG.heroCollection },
  { icon: Tractor, title: "Farmer Partnership", text: "We grow together. We work hand in hand with farmers, respecting their hard work and uplifting their communities.", image: IMG.heroFarm },
  { icon: Globe, title: "Sustainability", text: "We protect the earth that nourishes us. For today, tomorrow and for generations to come.", image: IMG.soilHands },
  { icon: FlaskConical, title: "Science", text: "We combine traditional wisdom with modern science for real, measurable health benefits.", image: IMG.moringaBowl },
];

export default function OurStory() {
  return (
    <div data-testid="our-story-page">
      <FoundingLeaders />

      <section className="py-20 sm:py-28">
        <div className="container-nb grid lg:grid-cols-3 gap-10 items-stretch">
          <Reveal className="flex flex-col justify-center">
            <p className="eyebrow mb-4">Why Nutribest?</p>
            <div className="space-y-4 text-sm sm:text-base text-mutedgreen leading-relaxed">
              <p>In today's world, food has become increasingly processed, labels more confusing, and trust harder to earn.</p>
              <p>We asked ourselves a simple question.</p>
              <p className="font-serif text-xl text-forest">Why should choosing healthy food feel uncertain?</p>
              <p>That question became the beginning of Nutribest. We imagined a company where families could choose nutrition with confidence, knowing that every product reflected the same care we would give our own loved ones.</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <ParallaxImage src={IMG.soilHands} alt="Hands holding a seedling" className="rounded-2xl h-72 lg:h-full shadow-xl shadow-forest/10" speed={10} />
          </Reveal>
          <Reveal delay={0.25}>
            <div className="h-full bg-white border border-forest/10 rounded-2xl p-8 flex flex-col justify-center text-center">
              <span className="mx-auto w-14 h-14 rounded-full border border-gold/50 flex items-center justify-center text-forest"><Leaf size={22} strokeWidth={1.5} /></span>
              <p className="eyebrow mt-6 !text-forest/60">Our Purpose</p>
              <h3 className="font-serif text-2xl sm:text-3xl text-forest mt-3 leading-snug">We exist to earn trust.</h3>
              <div className="w-10 h-px bg-gold mx-auto my-5" />
              <p className="text-sm text-mutedgreen leading-relaxed">Not to sell products.<br />Not to follow trends.<br />Not to become the biggest.<br /><span className="font-serif italic text-forest">Simply to earn trust.</span></p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-creamdark/40 border-y border-forest/10">
        <div className="container-nb">
          <SectionHead eyebrow="Our Beginning" title="A journey rooted in nature. Guided by people. Driven by a belief." />
          <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 relative">
            {BEGINNING.map(({ icon: Icon, title, text }, i) => (
              <StaggerItem key={title}>
                <div data-testid={`beginning-step-${i + 1}`} className="text-center relative">
                  {i < BEGINNING.length - 1 && <span className="hidden lg:block absolute top-8 left-[calc(50%+2.5rem)] w-[calc(100%-5rem)] border-t border-dashed border-gold/50" />}
                  <span className="relative mx-auto w-16 h-16 rounded-full bg-white border border-forest/15 flex items-center justify-center text-forest hover:border-gold hover:text-gold transition-colors duration-500">
                    <Icon size={22} strokeWidth={1.5} />
                  </span>
                  <h4 className="mt-4 text-[11px] font-bold uppercase tracking-[0.18em] text-forest">{title}</h4>
                  <p className="mt-2 text-[11px] text-mutedgreen leading-relaxed max-w-[180px] mx-auto">{text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="relative py-28 sm:py-40 overflow-hidden">
        <ParallaxImage src={IMG.heroStory} alt="" className="absolute inset-0" speed={12} />
        <div className="absolute inset-0 bg-forestdeep/70" />
        <div className="relative container-nb text-center max-w-4xl">
          <Reveal>
            <p className="eyebrow !text-goldlight mb-8">Our Philosophy</p>
            <blockquote data-testid="philosophy-quote" className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cream leading-[1.25]">
              "Nature has already created extraordinary nutrition. <span className="italic text-goldlight">Our responsibility is to preserve it — not change it.</span>"
            </blockquote>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="container-nb">
          <SectionHead eyebrow="Our Values. Our Way of Life." title="The principles behind every pack." />
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUE_CARDS.map(({ icon: Icon, title, text, image }) => (
              <StaggerItem key={title}>
                <div data-testid={`story-value-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group bg-white rounded-2xl border border-forest/10 overflow-hidden hover:shadow-xl hover:shadow-forest/10 hover:-translate-y-1 transition-all duration-500">
                  <div className="h-44 overflow-hidden">
                    <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2.5">
                      <Icon size={17} className="text-gold" strokeWidth={1.7} />
                      <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-forest">{title}</h3>
                    </div>
                    <p className="mt-3 text-xs sm:text-sm text-mutedgreen leading-relaxed">{text}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="container-nb pb-20 sm:pb-28">
        <div className="grid lg:grid-cols-2 rounded-3xl overflow-hidden border border-forest/10">
          <div className="relative min-h-[320px]">
            <ParallaxImage src={IMG.heroFarm} alt="Misty green mountains" className="absolute inset-0" speed={10} />
            <div className="absolute inset-0 bg-gradient-to-t from-forestdeep/70 to-transparent" />
            <div className="relative p-8 sm:p-12 h-full flex flex-col justify-end">
              <h3 className="font-serif text-3xl sm:text-4xl text-cream leading-tight">This is only<br />the beginning.</h3>
              <p className="mt-4 text-cream/80 text-sm max-w-sm leading-relaxed">We dream of building one of the world's most trusted premium superfoods brands. Proudly rooted in India. Respected across the globe.</p>
            </div>
          </div>
          <div className="bg-forestdeep grain relative p-8 sm:p-12 flex flex-col justify-center">
            <p className="eyebrow !text-goldlight mb-6">Our Promise</p>
            {[
              "If it isn't good enough for our own family, it isn't good enough for our customers.",
              "If it's from Nutribest, I don't need to worry about quality.",
            ].map((t) => (
              <div key={t} className="flex gap-4 mb-6 last:mb-0">
                <span className="shrink-0 w-10 h-10 rounded-full border border-goldlight/40 flex items-center justify-center text-goldlight"><ShieldCheck size={16} /></span>
                <p className="font-serif text-lg text-cream leading-snug">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="container-nb grid lg:grid-cols-2 gap-10 items-center bg-white border border-forest/10 rounded-3xl overflow-hidden">
          <div className="p-8 sm:p-12 flex items-center gap-6">
            <img src={SEAL} alt="Nutribest seal" className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-full mix-blend-multiply shrink-0" />
            <div>
              <h3 className="font-serif text-2xl sm:text-3xl text-forest leading-snug">We invite you to be part of our story.</h3>
              <p className="mt-3 text-sm text-mutedgreen leading-relaxed">Because Nutribest isn't complete without the families who place their trust in us.</p>
            </div>
          </div>
          <ParallaxImage src={IMG.familyDinner} alt="Family sharing a healthy meal" className="h-64 lg:h-80" speed={8} />
        </div>
      </section>

      <Marquee items={["Proudly rooted in India.", "Respected across the globe.", "Earned trust. Pure nutrition.", "Guided by people. Driven by belief."]} />
    </div>
  );
}
